import { useEffect, useRef, useState, type RefObject } from 'react'
import { Scene } from '@antv/l7'
import { Map } from '@antv/l7-maps'

export type L7MapOptions = {
  center?: [number, number]
  zoom?: number
}

/**
 * L7 Scene 生命周期封装（StrictMode 双挂载幂等）。
 * 内置 Map 只提供深色底 + 交互，瓦片由调用方通过 TileLayer 叠加。
 */
export function useL7Map(containerRef: RefObject<HTMLDivElement | null>, opts?: L7MapOptions) {
  const sceneRef = useRef<Scene | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    let disposed = false
    const scene = new Scene({
      id: container,
      logoVisible: false,
      map: new Map({
        center: [opts?.center?.[0] ?? 87.6, opts?.center?.[1] ?? 43.7],
        zoom: opts?.zoom ?? 7,
        style: 'dark',
      }),
    })
    sceneRef.current = scene
    scene.on('loaded', () => {
      if (disposed) return
      // L7 内置 Map 初始 center/zoom 偶发不生效，loaded 后强制校正一次
      scene.setCenter([opts?.center?.[0] ?? 87.6, opts?.center?.[1] ?? 43.7])
      scene.setZoom(opts?.zoom ?? 7)
      setReady(true)
    })
    return () => {
      disposed = true
      setReady(false)
      sceneRef.current = null
      void scene.destroy()
    }
    // Scene 仅随容器挂载创建一次，center/zoom 取初始值
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef])

  return { scene: sceneRef, ready }
}
